import { useCallback, useEffect, useState } from 'react';
import {
  applyMode,
  nextMode,
  readMode,
  resolveMode,
  systemPrefersDark,
  writeMode,
  type ThemeMode,
} from './themeMode.js';
import { Icon, type IconName } from '../mo/Icon.js';

const ICON: Record<ThemeMode, IconName> = {
  system: 'contrast',
  light: 'sun',
  dark: 'moon',
};

const LABEL: Record<ThemeMode, string> = {
  system: 'Theme: match system',
  light: 'Theme: light',
  dark: 'Theme: dark',
};

/**
 * Cycles system, light, dark.
 *
 * Rendered in the topbar as a plain `.icon-btn` so it inherits the shell styling
 * rather than introducing a control that looks foreign.
 *
 * The `system` state re-renders on OS change, which matters on a phone that switches
 * at sunset: without the listener the label would keep claiming light after the device
 * had gone dark, even though the palette had already followed the media query.
 */
export function ThemeToggle(): JSX.Element {
  const [mode, setMode] = useState<ThemeMode>(() => readMode());
  const [prefersDark, setPrefersDark] = useState<boolean>(() => systemPrefersDark());

  useEffect(() => {
    const mq = globalThis.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const onChange = (e: MediaQueryListEvent): void => setPrefersDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const cycle = useCallback(() => {
    setMode((current) => {
      const next = nextMode(current);
      writeMode(next);
      const root = globalThis.document?.documentElement;
      if (root) applyMode(next, root);
      return next;
    });
  }, []);

  const effective = resolveMode(mode, prefersDark);

  return (
    <button
      type="button"
      className="icon-btn"
      onClick={cycle}
      title={`${LABEL[mode]}${mode === 'system' ? ` (currently ${effective})` : ''}`}
      aria-label={LABEL[mode]}
      data-theme-mode={mode}
    >
      <Icon name={ICON[mode]} size={17} />
    </button>
  );
}
